/**
 * The comparison, client-side: what the panel edits, what the server takes, and
 * how to say out loud what it is going to do.
 *
 * Same split as filterSpec.js and resolveSpec.js — a draft shape that is
 * convenient to edit (Sets, a map keyed by column name) and a request shape that
 * is stable enough to cache on, with a signature so "you have unapplied changes"
 * is a comparison rather than a guess.
 */

/**
 * What the table lists once a comparison is running. Every one of these is a
 * slice of the same finished scan, so switching between them is free.
 */
export const COMPARE_MODES = [
  {
    id: 'all',
    label: 'Everything',
    verb: 'compared against',
    blurb: 'Every row of both sides, each flagged with what the uploaded file does to it.',
    scope: 'compared rows',
    exportLabel: 'Export comparison',
    exportHint: 'Every row, with its verdict and the columns that differ',
    empty: 'Nothing to compare.',
  },
  {
    id: 'changed',
    label: 'Changed values',
    action: 'Show what changed',
    verb: 'showing values changed by',
    blurb: 'Rows that exist on both sides but disagree — and on which columns.',
    scope: 'changed rows',
    exportLabel: 'Export changes',
    exportHint: 'The rows whose values differ, with the columns that differ named',
    empty: 'Every matching row agrees on every compared column.',
  },
  {
    id: 'missing',
    label: 'Missing from the file',
    action: 'Show what is missing',
    verb: 'showing rows absent from',
    blurb: 'Rows the view has that the uploaded file does not have at all.',
    scope: 'rows missing from the file',
    exportLabel: 'Export missing rows',
    exportHint: 'Rows in your view with no counterpart in the uploaded file',
    empty: 'The uploaded file covers every row of the view.',
  },
  {
    id: 'extra',
    label: 'Only in the file',
    action: 'Show what is extra',
    verb: 'showing rows only in',
    blurb: 'Rows the uploaded file has that the view does not — new records, or rows your filters exclude.',
    scope: 'rows only in the file',
    exportLabel: 'Export extra rows',
    exportHint: 'Rows in the uploaded file with no counterpart in your view',
    empty: 'The uploaded file has nothing the view does not.',
  },
  {
    id: 'same',
    label: 'Unchanged',
    action: 'Show what is unchanged',
    verb: 'showing rows unchanged by',
    blurb: 'Rows that match on the key and agree on every compared column.',
    scope: 'unchanged rows',
    exportLabel: 'Export unchanged',
    exportHint: 'Rows the uploaded file agrees with exactly',
    empty: 'Nothing matched without a difference.',
  },
  {
    id: 'repeat',
    label: 'Repeated keys',
    action: 'Show repeated keys',
    verb: 'showing repeated keys against',
    blurb: 'Rows whose key repeats within their own side, so nothing could be paired with them.',
    scope: 'rows with a repeated key',
    exportLabel: 'Export repeated keys',
    exportHint: 'Rows that share a key with another row on the same side',
    empty: 'Every key appears at most once on each side.',
  },
]

export const compareMode = (id) => COMPARE_MODES.find((m) => m.id === id) ?? COMPARE_MODES[0]

/** How a verdict reads in the table, and what it means. */
export const VERDICTS = {
  same: { mark: '=', label: 'same', hint: 'Matched on the key, and every compared column agrees' },
  changed: { mark: '≠', label: 'changed', hint: 'Matched on the key, but at least one compared column differs' },
  missing: { mark: '−', label: 'missing', hint: 'In your view, with no counterpart in the uploaded file' },
  extra: { mark: '+', label: 'extra', hint: 'In the uploaded file, with no counterpart in your view' },
  repeat: { mark: '⧉', label: 'repeat', hint: 'This key repeats on its own side, so nothing could be paired with it' },
}

export const DEFAULT_OPTIONS = {
  trim: true,
  ignoreCase: false,
  numeric: false,
  dates: false,
  dayFirst: false,
  blanks: 'strict',
}

/**
 * How each comparison option reads in the panel. `needs` hides an option that
 * only means something alongside another one — day-first is a question about
 * dates, and asking it while date matching is off is noise.
 */
export const COMPARE_OPTIONS = [
  { id: 'trim', label: 'ignore surrounding spaces', hint: '“ Ann ” and “Ann” are the same value.' },
  { id: 'ignoreCase', label: 'ignore case', hint: '“WON” and “won” are the same value.' },
  { id: 'numeric', label: 'match numbers as numbers', hint: '1.0, 1 and 1.00 are the same value.' },
  { id: 'dates', label: 'match dates as dates', hint: '2026-02-11 and 11 Feb 2026 are the same value.' },
  { id: 'dayFirst', label: 'read 01/02 as 1 February', hint: 'Only settles the dates that are genuinely ambiguous.', needs: 'dates' },
]

export function emptyCompare() {
  return { map: {}, keyColumns: [], columns: [], options: { ...DEFAULT_OPTIONS } }
}

/** Header names reduced to what a human would call the same column. */
const loose = (name) => String(name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')

/**
 * A first guess at the draft for a file that has just been uploaded: the server's
 * suggested mapping and key, every mapped non-key column compared, and the
 * default options. Only ever a starting point — the panel shows all of it.
 */
export function initialCompare(viewHeaders, upload) {
  const map = { ...(upload?.suggestedMap ?? {}) }
  const keyColumns = (upload?.suggestedMatchKey ?? []).filter((c) => map[c])
  const columns = viewHeaders.filter((c) => map[c] && !keyColumns.includes(c))
  return { map, keyColumns, columns, options: { ...DEFAULT_OPTIONS } }
}

/**
 * Keep a draft usable after the uploaded file changes underneath it: a mapping to
 * a column the new file does not have, and anything selected through it, has to
 * go rather than be sent to the server, which would reject the whole request.
 *
 * @returns {{compare, dropped: number}}
 */
export function reconcileCompare(draft, viewHeaders, fileHeaders) {
  const known = new Set(viewHeaders)
  const theirs = new Set(fileHeaders)
  const map = {}
  let dropped = 0
  for (const [name, other] of Object.entries(draft?.map ?? {})) {
    if (known.has(name) && theirs.has(other)) map[name] = other
    else dropped++
  }
  // A column the guess can still pair up is better than one silently left out.
  for (const [name, other] of Object.entries(autoMap(viewHeaders, fileHeaders))) {
    if (!map[name] && !Object.values(map).includes(other)) map[name] = other
  }
  const keyColumns = (draft?.keyColumns ?? []).filter((c) => map[c])
  const columns = (draft?.columns ?? []).filter((c) => map[c] && !keyColumns.includes(c))
  return {
    compare: { map, keyColumns, columns, options: { ...DEFAULT_OPTIONS, ...(draft?.options ?? {}) } },
    dropped,
  }
}

/**
 * Pair up the two files' columns by name: exact first, then ignoring case,
 * spaces and punctuation, so `BCL Status` finds `bcl_status`.
 *
 * Mirrors the server's autoMap — the client needs it to re-pair a draft after a
 * file swap without a round trip, and the two must agree on what the guess is.
 */
export function autoMap(viewHeaders, fileHeaders) {
  const taken = new Set()
  const byLoose = new Map()
  for (const name of fileHeaders) {
    const key = loose(name)
    if (!byLoose.has(key)) byLoose.set(key, name)
  }
  const map = {}
  for (const name of viewHeaders) {
    if (fileHeaders.includes(name) && !taken.has(name)) {
      map[name] = name
      taken.add(name)
    }
  }
  for (const name of viewHeaders) {
    if (map[name]) continue
    const hit = byLoose.get(loose(name))
    if (hit && !taken.has(hit)) {
      map[name] = hit
      taken.add(hit)
    }
  }
  return map
}

/**
 * The shape the server takes, or null when the draft cannot ask a question yet.
 *
 * A comparison with no key has nothing to pair rows on, which is the one part
 * that has no sensible default — matching on "the whole row" would report every
 * edited row as one missing and one extra, which is not an answer.
 */
export function toRequestCompare(draft) {
  if (!draft?.keyColumns?.length) return null
  const map = {}
  for (const [name, other] of Object.entries(draft.map ?? {})) {
    if (typeof other === 'string' && other !== '') map[name] = other
  }
  if (!draft.keyColumns.every((c) => map[c])) return null
  const options = { ...DEFAULT_OPTIONS, ...(draft.options ?? {}) }
  // Day-first is a question about dates; carrying it while date matching is off
  // would make two identical comparisons look different and rescan for nothing.
  if (!options.dates) options.dayFirst = false
  return {
    keyColumns: [...draft.keyColumns],
    map,
    columns: (draft.columns ?? []).filter((c) => map[c] && !draft.keyColumns.includes(c)),
    options,
  }
}

/**
 * Stable identity, so "nothing has changed" is a string comparison.
 *
 * Deliberately total: this is called on every render against both the draft and
 * whatever is applied, and a signature that threw on a half-built spec would take
 * the panel down rather than report "not the same yet".
 */
export function compareSignature(spec) {
  if (!spec || typeof spec !== 'object') return ''
  return JSON.stringify([
    spec.keyColumns ?? [],
    Object.entries(spec.map ?? {}).sort(([a], [b]) => a.localeCompare(b)),
    [...(spec.columns ?? [])].sort(),
    spec.options ?? {},
  ])
}

/** The columns of the view that have a counterpart in the uploaded file. */
export function mappedColumns(draft, viewHeaders) {
  return viewHeaders.filter((name) => !!draft?.map?.[name])
}

/**
 * What the comparison is going to do, in plain words, so the stack of checkboxes
 * above never has to be read back one control at a time.
 *
 * @returns {{lead: string, lines: string[]}}
 */
export function describeCompare(draft, fileName) {
  const spec = toRequestCompare(draft)
  const where = fileName ? `“${fileName}”` : 'the uploaded file'
  if (!spec) {
    return {
      lead: `Nothing to compare ${where} on yet.`,
      lines: ['Pick at least one column that identifies a row on both sides.'],
    }
  }

  const key = spec.keyColumns.join(' + ')
  const count = spec.columns.length
  const lines = [
    count === 0
      ? `No columns are being compared, so every paired row reads as unchanged — only what is missing and what is extra will show.`
      : `Rows are paired on ${key}, and ${count} column${count === 1 ? '' : 's'} ${count === 1 ? 'is' : 'are'} compared.`,
  ]

  const how = []
  if (spec.options.trim) how.push('surrounding spaces ignored')
  if (spec.options.ignoreCase) how.push('case ignored')
  if (spec.options.numeric) how.push('numbers matched as numbers')
  if (spec.options.dates) how.push(spec.options.dayFirst ? 'dates matched, day first' : 'dates matched as dates')
  if (spec.options.blanks === 'file') how.push('a blank in the file is not a change')
  if (how.length) lines.push(`${how.join(', ').replace(/^./, (c) => c.toUpperCase())}.`)

  lines.push(
    'The view is the base: a row it has and the file does not is missing, and a row only the file has is extra.',
  )
  return { lead: `Every row of the view, checked against ${where}.`, lines }
}
